const SensorBasedAlignment = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/sensor-alignment.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            The Future of Sensor-Based Wheel Alignment
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Sensor-driven alignment technology is transforming accuracy, speed, and vehicle safety. Here’s how modern alignment is evolving — and what the future holds.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            Wheel alignment has come a long way from manual measurements and visual inspection. As vehicles become smarter and more advanced, alignment systems are evolving too. Sensor-based wheel alignment — powered by cameras, lasers, and real-time data — is rapidly becoming the industry standard.
            At <strong>Express Tire Works</strong>, we stay ahead of technology to ensure precision, efficiency, and safety. Here's a deep dive into how sensor-based alignment is transforming the automotive world and what innovations lie ahead.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. What Is Sensor-Based Wheel Alignment?
            </h2>
            <p>
              Sensor-based alignment uses high-resolution cameras, lasers, and digital sensors to capture wheel angles with extreme precision. These systems replace manual tools with automated, real-time measurements.
            </p>
            <p className="mt-3">Most advanced systems analyze three key angles:</p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Camber:</strong> Tire tilt in or out</li>
              <li><strong>Caster:</strong> Steering pivot angle</li>
              <li><strong>Toe:</strong> Direction wheels point inward/outward</li>
            </ul>
            <p>
              Modern scanners measure these angles down to fractions of a degree — allowing technicians to correct alignment with unmatched accuracy.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. Benefits of Sensor-Based Alignment
            </h2>
            <p>
              Sensor technology offers several advantages over traditional alignment methods, improving both precision and efficiency.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Higher accuracy:</strong> Laser and camera sensors eliminate human error.</li>
              <li><strong>Faster service:</strong> Digital readings significantly speed up the process.</li>
              <li><strong>Real-time diagnostics:</strong> Systems instantly detect misalignment and show required adjustments.</li>
              <li><strong>Better tire lifespan:</strong> Prevents uneven or premature tire wear.</li>
              <li><strong>Improved fuel efficiency:</strong> Proper alignment reduces rolling resistance.</li>
            </ul>
            <p>
              With precise sensor data, technicians can fine-tune alignment with pinpoint accuracy.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. How Sensor Technology Is Evolving
            </h2>
            <p>
              Sensor-based alignment continues to improve as vehicles become more advanced. The next wave of innovation integrates smarter tools, automation, and greater connectivity.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Artificial intelligence:</strong> Predicts alignment patterns based on vehicle data.</li>
              <li><strong>4D imaging:</strong> Tracks wheel angles in real-time, even as the vehicle moves.</li>
              <li><strong>Wireless sensors:</strong> Reduce setup time and eliminate calibration cables.</li>
              <li><strong>Cloud-based reporting:</strong> Stores alignment history for easy access and analysis.</li>
              <li><strong>Vehicle-to-equipment (V2E) communication:</strong> Cars share data directly with alignment systems.</li>
            </ul>
            <p>
              These innovations increase accuracy, reduce time, and make alignment more intuitive for both shops and drivers.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. Alignment & ADAS: A New Era of Precision
            </h2>
            <p>
              Modern vehicles include ADAS (Advanced Driver Assistance Systems) like lane-keeping, adaptive cruise control, and collision avoidance. These systems rely on accurate wheel angles to function safely.
            </p>
            <p className="mt-3">
              Sensor-based alignment ensures ADAS cameras, radars, and sensors work correctly by aligning wheels with exact manufacturer specifications.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Improved camera calibration</li>
              <li>Accurate sensor readings</li>
              <li>Better lane detection</li>
              <li>Enhanced overall safety</li>
            </ul>
            <p>
              As ADAS becomes standard, sensor-based wheel alignment will be essential, not optional.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              5. The Future of Sensor-Based Alignment
            </h2>
            <p>
              The alignment industry is moving toward greater automation, digitization, and predictive maintenance. Here's what the future will likely look like:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Fully automated alignment bays:</strong> Vehicles self-position while systems scan automatically.</li>
              <li><strong>Self-correcting wheel systems:</strong> Future EVs may adjust alignment electronically in real-time.</li>
              <li><strong>Predictive wear analytics:</strong> AI forecasts when misalignment will occur.</li>
              <li><strong>Integration with EV smart systems:</strong> Wheel alignment tied to battery efficiency and tire health.</li>
            </ul>
            <p>
              As technology advances, alignment will become faster, more accurate, and more connected to the vehicle's onboard systems.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              Sensor-based wheel alignment is rapidly transforming the industry — offering unmatched precision, speed, and long-term vehicle safety. As ADAS, AI, and automation continue to evolve, alignment systems will become smarter and more integrated than ever before.
              At <strong>Express Tire Works</strong>, we use industry-leading sensor technology to ensure your vehicle’s wheels stay aligned with exact accuracy, delivering a smoother, safer, and more efficient driving experience.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Sensor Alignment</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">ADAS Calibration</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Automotive Technology</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Wheel Alignment</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Future Tech</p>
        </div>
      </section>
    </div>
  );
};

export default SensorBasedAlignment;